import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Archive, Download, Loader2, ShieldCheck, Beaker, RefreshCw, ScrollText, ShieldAlert } from "lucide-react";

const REASONS = ["Test Data", "Duplicate", "Entered in Error", "Failed Import", "Abandoned Draft", "Customer Record Created in Error", "Other"];

export default function TestDataCleanup({ me }) {
  const isSuper = me?.caps?.manage_roles && me?.recon_role === "super_admin";
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({});
  const [groupBy, setGroupBy] = useState("module");
  const [confirm, setConfirm] = useState(null); // {action}
  const [audit, setAudit] = useState(null);

  const load = useCallback(() => {
    if (!isSuper) return;
    setLoading(true);
    api.get("/removal/cleanup").then((r) => setInv(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [isSuper]);
  useEffect(() => { load(); }, [load]);

  const items = inv?.items || [];
  const key = (i) => `${i.type}:${i.id}`;
  const selectedItems = items.filter((i) => sel[key(i)]);
  const toggle = (i) => setSel((s) => ({ ...s, [key(i)]: !s[key(i)] }));
  const groups = useMemo(() => {
    const g = {};
    const field = { module: "module", customer: "customer", type: "label", created_by: "created_by" }[groupBy];
    items.forEach((i) => { const k = i[field] || "—"; (g[k] = g[k] || []).push(i); });
    return g;
  }, [items, groupBy]);

  const selectGroup = (arr, on) => setSel((s) => { const n = { ...s }; arr.forEach((i) => { n[key(i)] = on; }); return n; });

  const exportCsv = () => {
    const rows = [["Type", "Name", "Customer", "Status", "Classification", "Created By", "Created", "Eligible", "Protected", "Flags"]];
    (selectedItems.length ? selectedItems : items).forEach((i) => rows.push([
      i.label, i.name, i.customer, i.status, i.classification, i.created_by, String(i.created_at).slice(0, 10),
      i.eligible_permanent ? "Yes" : "No", i.protected ? "Yes" : "No", (i.flags || []).join("; "),
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `test-data-inventory-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const openAudit = async () => {
    try { const r = await api.get("/removal/audit"); setAudit(r.data.entries || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!isSuper) return null;

  return (
    <section className="rounded-md border border-border bg-card" data-testid="test-cleanup-section">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2"><Beaker className="h-4 w-4 text-gold-dark" /><h2 className="font-display font-bold text-navy">Test Data Cleanup</h2></div>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500"><ShieldCheck className="h-4 w-4" /> Super Admin only</span>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Surfaces records that look like test, demo, or training data using classification and heuristics (names/customers containing test-like keywords, automated-account creators, reconciliations with no finalized snapshot or approved findings). Nothing is changed automatically — review, then reclassify (via each record's actions), archive, or permanently delete eligible records. Production records are never deleted here.
        </p>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-48 space-y-1"><Label className="text-xs">Group by</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger data-testid="cleanup-groupby"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="module">Module</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="type">Record type</SelectItem>
                  <SelectItem value="created_by">Created by</SelectItem>
                </SelectContent></Select></div>
            <Button variant="outline" size="sm" onClick={load} className="mt-5" data-testid="cleanup-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</Button>
            <Button variant="outline" size="sm" onClick={openAudit} className="mt-5" data-testid="cleanup-audit-btn"><ScrollText className="h-3.5 w-3.5 mr-1.5" /> Deletion Audit</Button>
          </div>
          {inv && (
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>{inv.total} candidates</span><span className="text-emerald-700">{inv.eligible} eligible</span><span className="text-amber-700">{inv.protected} protected</span>
            </div>
          )}
        </div>

        {loading ? <div className="py-10 text-center text-muted-foreground text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Scanning…</div> : (
          <div className="space-y-4 max-h-[28rem] overflow-y-auto">
            {Object.keys(groups).length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No test-data candidates found.</p>}
            {Object.entries(groups).map(([gname, arr]) => (
              <div key={gname} className="rounded border border-border">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
                  <span className="text-xs font-semibold text-navy">{gname} ({arr.length})</span>
                  <div className="flex gap-2">
                    <button className="text-[11px] text-gold-dark" onClick={() => selectGroup(arr, true)}>Select all</button>
                    <button className="text-[11px] text-muted-foreground" onClick={() => selectGroup(arr, false)}>Clear</button>
                  </div>
                </div>
                <div>
                  {arr.map((i) => (
                    <label key={key(i)} className="flex items-start gap-2 px-3 py-2 border-b border-border/50 last:border-0 hover:bg-slate-50 cursor-pointer text-xs" data-testid="cleanup-row">
                      <Checkbox checked={!!sel[key(i)]} onCheckedChange={() => toggle(i)} className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-navy truncate">{i.name || i.id}</span>
                          <span className="text-muted-foreground">· {i.label}</span>
                          {i.customer && <span className="text-muted-foreground">· {i.customer}</span>}
                          {i.classification !== "Production" && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">{i.classification}</span>}
                          {i.protected ? <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1"><ShieldAlert className="h-3 w-3" />Protected</span>
                            : i.eligible_permanent ? <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Eligible</span> : null}
                        </div>
                        <div className="text-[11px] text-amber-700 mt-0.5">Flagged: {(i.flags || []).join(" · ")}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border">
          <span className="text-xs text-muted-foreground mr-auto">{selectedItems.length} selected</span>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={items.length === 0} data-testid="cleanup-export"><Download className="h-3.5 w-3.5 mr-1.5" /> Export Inventory</Button>
          <Button variant="outline" size="sm" onClick={() => setConfirm({ action: "archive" })} disabled={selectedItems.length === 0} data-testid="cleanup-archive"><Archive className="h-3.5 w-3.5 mr-1.5" /> Archive Selected</Button>
          <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={() => setConfirm({ action: "delete" })} disabled={selectedItems.length === 0} data-testid="cleanup-delete"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete Selected</Button>
        </div>
      </div>

      {confirm && <CleanupConfirm action={confirm.action} items={selectedItems} onClose={() => setConfirm(null)} onDone={() => { setConfirm(null); setSel({}); load(); }} />}
      {audit !== null && <AuditDialog entries={audit} onClose={() => setAudit(null)} />}
    </section>
  );
}

function CleanupConfirm({ action, items, onClose, onDone }) {
  const isDelete = action === "delete";
  const [reason, setReason] = useState(isDelete ? "Test Data" : "Test data cleanup");
  const [explanation, setExplanation] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const eligible = items.filter((i) => i.eligible_permanent && i.classification !== "Production");
  const notEligible = items.length - eligible.length;

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/removal/cleanup/execute", {
        items: items.map((i) => ({ type: i.type, id: i.id })), action,
        reason: isDelete ? reason : (reason || "Test data cleanup"), explanation,
      });
      toast.success(`Cleanup complete — ${r.data.deleted} deleted, ${r.data.archived} archived, ${r.data.blocked} blocked.`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed."); }
    finally { setBusy(false); }
  };
  const ready = !busy && (!isDelete || (reason && (reason !== "Other" || explanation.trim()) && confirmText === "DELETE"));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="cleanup-confirm-dialog">
        <DialogHeader>
          <DialogTitle className={isDelete ? "text-red-700" : "text-navy"}>{isDelete ? "Permanently delete" : "Archive"} {items.length} record(s)</DialogTitle>
          <DialogDescription>{isDelete ? "Only records classified as Test Data / Training and eligible will be deleted. Production and protected records are skipped." : "Selected records will be archived and preserved."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {isDelete && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <b>{eligible.length}</b> will be deleted. <b>{notEligible}</b> will be skipped (Production classification or protected). Reclassify records as Test Data first if you intend to delete them.
            </div>
          )}
          {(isDelete) && (
            <div className="space-y-1.5"><Label className="text-xs">Deletion reason</Label>
              <Select value={reason} onValueChange={setReason}><SelectTrigger data-testid="cleanup-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
          )}
          {isDelete && reason === "Other" && (
            <div className="space-y-1.5"><Label className="text-xs">Explanation</Label><Textarea rows={2} value={explanation} onChange={(e) => setExplanation(e.target.value)} /></div>
          )}
          {isDelete && (
            <div className="space-y-1.5"><Label className="text-xs">Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" data-testid="cleanup-confirm-text" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className={isDelete ? "bg-red-600 text-white hover:bg-red-700" : "bg-navy text-white hover:bg-navy-700"} disabled={!ready} onClick={run} data-testid="cleanup-confirm-run">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} {isDelete ? "Delete Eligible" : "Archive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditDialog({ entries, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="deletion-audit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ScrollText className="h-5 w-5 text-gold" /> Deletion & Lifecycle Audit</DialogTitle>
          <DialogDescription>Immutable record of every removal, archive, void, restore, and reclassification. This log cannot be edited or deleted.</DialogDescription></DialogHeader>
        <div className="rounded border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b text-left">{["When", "Action", "Type", "Record", "By", "Reason"].map((h) => <th key={h} className="px-2 py-2 font-semibold text-navy">{h}</th>)}</tr></thead>
            <tbody>
              {entries.length === 0 ? <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No removal actions yet.</td></tr> :
                entries.map((e) => (
                  <tr key={e.id} className="border-b border-border/50 last:border-0">
                    <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">{String(e.at).slice(0, 16).replace("T", " ")}</td>
                    <td className="px-2 py-1.5 font-medium text-navy">{e.action}</td>
                    <td className="px-2 py-1.5">{e.record_type}</td>
                    <td className="px-2 py-1.5 truncate max-w-[160px]">{e.record_name}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{e.by}</td>
                    <td className="px-2 py-1.5">{e.reason}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
